'use strict';
import { formatters, validators } from '@utils/validation.js';
import * as THREE from 'three';

class UIElement {
  constructor(dom) {
    this.dom = dom;

    // Store a reference to this UIElement instance on the DOM element
    // This allows the clear() method to find and call destroy() methods
    if (dom) {
      dom._uiElement = this;
    }
  }

  add() {
    for (let i = 0; i < arguments.length; i++) {
      const argument = arguments[i];

      if (argument instanceof UIElement) {
        this.dom.appendChild(argument.dom);
      } else {
        console.error(
          'UIElement:',
          argument,
          'is not an instance of UIElement.',
        );
      }
    }

    return this;
  }

  addFront() {
    for (let i = 0; i < arguments.length; i++) {
      const argument = arguments[i];

      if (argument instanceof UIElement) {
        this.dom.prepend(argument.dom);
      } else {
        console.error(
          'UIElement:',
          argument,
          'is not an instance of UIElement.',
        );
      }
    }

    return this;
  }

  remove() {
    if (arguments.length === 0) {
      if (this.dom.parentNode) {
        this.dom.parentNode.removeChild(this.dom);
      }
    } else {
      for (let i = 0; i < arguments.length; i++) {
        const argument = arguments[i];

        if (argument instanceof UIElement) {
          this.dom.removeChild(argument.dom);
        } else {
          console.error(
            'UIElement:',
            argument,
            'is not an instance of UIElement.',
          );
        }
      }
    }
    return this;
  }

  clear() {
    if (!this.dom || !this.dom.children) return;
    while (this.dom.children.length) {
      this.dom.removeChild(this.dom.lastChild);
    }
  }

  /**
   * Base destroy method for cleanup
   * Can be overridden by subclasses for custom cleanup logic
   */
  destroy() {
    // Remove reference from DOM element
    if (this.dom && this.dom._uiElement === this) {
      this.dom._uiElement = null;
    }

    // Only clear children and remove from parent if not being cleared by parent
    if (!this._isBeingCleared) {
      // Clear any children first
      this.clear();

      // Remove this element from its parent
      if (this.dom && this.dom.parentNode) {
        this.dom.parentNode.removeChild(this.dom);
      }
    }

    // Always clear DOM reference
    this.dom = null;
  }

  getId() {
    return this.dom.id;
  }

  setClass(name) {
    this.dom.className = name;

    return this;
  }

  addClass(name) {
    name.split(' ').forEach(className => {
      this.dom.classList.add(className);
    });

    return this;
  }

  removeClass(names) {
    names.split(' ').forEach(name => this.dom.classList.remove(name));

    return this;
  }

  toggleClass(name, force) {
    name.split(' ').forEach(className => {
      this.dom.classList.toggle(className, force);
    });

    return this;
  }

  hasClass(name) {
    return this.dom.classList.contains(name);
  }

  setStyle(style, array) {
    for (let i = 0; i < array.length; i++) {
      this.dom.style[style] = array[i];
    }

    return this;
  }

  setValue(value) {
    if (value !== undefined) {
      if (this.dom instanceof HTMLInputElement) {
        this.dom.value = value;
      } else {
        this.dom.textContent = value;
      }
    }
    return this;
  }

  setAttribute(data, value) {
    this.dom.setAttribute(data, value);

    return this;
  }

  getAttributeValue(value) {
    return this.dom[value];
  }

  getInnerHTML() {
    return this.dom.innerHTML;
  }

  setInnerHTML(html) {
    this.dom.innerHTML = html;
  }

  getInnerText() {
    return this.dom.innerText;
  }

  getIndexOfChild(element) {
    return Array.prototype.indexOf.call(this.dom.children, element.dom);
  }

  getChildren() {
    if (!this.dom || !this.dom.children) {
      return [];
    }
    return Array.from(this.dom.children);
  }

  dataset(name, data) {
    if (data !== undefined) {
      this.dom.dataset[name] = data;
    } else {
      return this.dom.dataset[name];
    }

    return this;
  }

  insertAt(uiElement, index) {
    const children = this.dom.children;

    this.dom.insertBefore(uiElement.dom, children[index]);

    return this;
  }

  contains(element) {
    return this.dom.contains(element);
  }

  getCssValue(property) {
    return window.getComputedStyle(this.dom).getPropertyValue(property);
  }

  find(selector) {
    let items = Array.from(this.dom.querySelectorAll(selector));
    items = items.map(item => {
      return new UIElement(item);
    });
    return items;
  }

  addFront() {
    for (let i = 0; i < arguments.length; i++) {
      const argument = arguments[i];
      if (argument instanceof UIElement) {
        this.dom.prepend(argument.dom);
      } else {
        console.error(
          'UIElement:',
          argument,
          'is not an instance of UIElement.',
        );
      }
    }
    return this;
  }

  getBoundingClientRect() {
    return this.dom.getBoundingClientRect();
  }

  scrollIntoView(
    options = { behavior: 'smooth', block: 'start', inline: 'nearest' },
    delay = 100,
  ) {
    setTimeout(() => {
      if (this.dom) {
        this.dom.scrollIntoView(options);
      }
    }, delay);
    return this;
  }

  getInnerDimensions() {
    const style = getComputedStyle(this.dom),
      { width, height } = this.dom.getBoundingClientRect();

    return {
      width:
        width - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight),
      height:
        height - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom),
    };
  }

  insertAdjacentElement(position, element) {
    if (element instanceof UIElement) {
      this.dom.insertAdjacentElement(position, element.dom);
    } else {
      console.error('UIElement:', element, 'is not an instance of UIElement.');
    }

    return this;
  }
}

// dom properties
const elemProperties = [
  'autocomplete',
  'checked',
  'disabled',
  'id',
  'innerHTML',
  'min',
  'max',
  'placeholder',
  'textContent',
  'innerText',
  'step',
  'title',
  'type',
  'spellcheck',
];
elemProperties.forEach(function (property) {
  const method =
    'set' +
    property.substr(0, 1).toUpperCase() +
    property.substr(1, property.length);

  UIElement.prototype[method] = function (value) {
    this.dom[property] = value;
    return this;
  };

  const getMethod =
    'get' +
    property.substr(0, 1).toUpperCase() +
    property.substr(1, property.length);

  UIElement.prototype[getMethod] = function (value) {
    return this.dom[property];
  };
});

// css properties
const properties = [
  'position',
  'left',
  'top',
  'right',
  'bottom',
  'width',
  'height',
  'display',
  'verticalAlign',
  'overflow',
  'overflowY',
  'overflowX',
  'color',
  'background',
  'backgroundColor',
  'backgroundImage',
  'backgroundSize',
  'opacity',
  'border',
  'borderLeft',
  'borderTop',
  'borderRight',
  'borderBottom',
  'borderColor',
  'borderRadius',
  'margin',
  'marginLeft',
  'marginTop',
  'marginRight',
  'marginBottom',
  'padding',
  'paddingLeft',
  'paddingTop',
  'paddingRight',
  'paddingBottom',
  'fontSize',
  'fontWeight',
  'textAlign',
  'textDecoration',
  'textTransform',
  'cursor',
  'animation',
  'zIndex',
  'gridTemplateRows',
  'gridTemplateColumns',
  'gridArea',
  'gridColumn',
  'gridRow',
  'pointerEvents',
  'maxWidth',
  'minWidth',
  'transform',
  'justifyContent',
  'alignItems',
  'placeSelf',
  'flexDirection',
  'order',
  'filter',
];

properties.forEach(function (property) {
  const method =
    'set' +
    property.substr(0, 1).toUpperCase() +
    property.substr(1, property.length);

  UIElement.prototype[method] = function () {
    this.setStyle(property, arguments);

    return this;
  };
});

// events

const events = [
  'KeyUp',
  'KeyDown',
  'MouseOver',
  'MouseOut',
  'PointerDown',
  'PointerMove',
  'PointerUp',
  'Click',
  'DblClick',
  'Change',
  'Input',
  'MouseDown',
  'MouseUp',
  'MouseEnter',
  'MouseLeave',
  'TouchStart',
  'TouchEnd',
  'Focus',
  'Blur',
  'TouchMove',
  'Drop',
  'DragStart',
  'DragEnd',
  'DragOver',
  'DragLeave',
  'Submit',
  'Paste',
];

events.forEach(function (event) {
  const method = 'on' + event;
  const triggerMethod = 'trigger' + event;

  // Adds an event listener method like onClick, onMouseOver, etc.
  UIElement.prototype[method] = function (callback, options = {}) {
    this.dom.addEventListener(
      event.toLowerCase(),
      callback.bind(this),
      options,
    );
    return this;
  };

  // Adds a trigger method like triggerClick, triggerMouseOver, etc.
  UIElement.prototype[triggerMethod] = function () {
    const eventObject = new Event(event.toLowerCase());
    this.dom.dispatchEvent(eventObject);
    return this;
  };
});

class UISpan extends UIElement {
  constructor(value) {
    super(document.createElement('span'));
    this.dom.textContent = value;
  }
}

class UIDiv extends UIElement {
  constructor() {
    super(document.createElement('div'));
  }
}

class UIRow extends UIDiv {
  constructor() {
    super();

    this.dom.className = 'Row';
  }
}

class UIPanel extends UIDiv {
  constructor() {
    super();

    this.dom.className = 'Panel';
  }
}

class UIText extends UISpan {
  constructor(text, noStyling = false) {
    super();

    if (noStyling) {
      this.dom.className = 'Text';
      this.dom.style.cursor = 'default';
      this.dom.style.display = 'inline-block';
    }

    this.setValue(text);
  }

  getValue() {
    return this.dom.textContent;
  }

  setValue(value) {
    if (value !== undefined) {
      this.dom.textContent = value;
    }

    return this;
  }
}

class UIInput extends UIElement {
  constructor(
    text,
    options = {
      defaultStyle: true,
    },
  ) {
    super(document.createElement('input'));

    this.dom.className = 'Input';

    this.dom.setAttribute('autocomplete', 'off');
    this.border = options.border;

    const typeMapping = {
      name: { formatter: formatters.name, validator: validators.name },
      email: { formatter: formatters.email, validator: validators.email },
      phone: {
        formatter: formatters.phone,
        validator: value =>
          validators.phone(value, options.countryCode || '+1'),
      },
      number: { formatter: formatters.number, validator: validators.number },
      currency: {
        formatter: formatters.currency,
        validator: validators.currency,
      },
      password: {
        formatter: formatters.string,
        validator: validators.password,
      },
      required: {
        formatter: formatters.string,
        validator: validators.required,
      },
      string: { formatter: formatters.string, validator: () => true },
    };

    let formatter, validator;
    if (options.type && typeMapping[options.type]) {
      formatter = typeMapping[options.type].formatter;
      validator = typeMapping[options.type].validator;
    } else {
      formatter = formatters.string;
      validator = () => true;
    }

    this.formatter = formatter;
    this.validator = validator;
    this.isValid = true;
    this.rawValue = '';
    this.touched = false; // Track if user has interacted

    if (options.defaultStyle === true) {
      this.dom.style.transition = 'border-color 0.2s, box-shadow 0.2s';
      this.dom.style.outline = 'none';
      this.dom.style.border = 'none';
    }

    this.dom.addEventListener('keydown', function (event) {
      event.stopPropagation();
    });

    let cursorPosition = 0;
    this.dom.addEventListener('input', e => {
      cursorPosition = e.target.selectionStart;
      const value = e.target.value;
      this.rawValue = this.formatter.unformat(value);
      const formatted = this.formatter.format(this.rawValue);
      if (formatted !== value) {
        const lengthDiff = formatted.length - value.length;
        e.target.value = formatted;
        if (
          this.dom.type === 'text' ||
          this.dom.type === 'tel' ||
          this.dom.type === 'search'
        ) {
          try {
            const newPosition = cursorPosition + lengthDiff;
            e.target.setSelectionRange(newPosition, newPosition);
          } catch (err) {}
        }
      }

      if (!this.touched) {
        this.touched = true;
      }
      this.isValid = this.validator(this.rawValue);
      this.#updateValidationStyle();
    });

    this.setValue(text);
  }

  getValue() {
    return this.rawValue;
  }

  getDisplayValue() {
    return this.dom.value;
  }

  setValue(value) {
    if (value !== undefined) {
      this.rawValue = this.formatter.unformat(value?.toString() || '');
      const formatted = this.formatter.format(this.rawValue);
      this.dom.value = formatted;
      this.isValid = this.validator(this.rawValue);
      this.#updateValidationStyle();
    }
    return this;
  }

  validate() {
    this.touched = true;
    this.isValid = this.validator(this.rawValue);
    this.#updateValidationStyle();
    return this.isValid;
  }

  #updateValidationStyle() {
    const type = this.dom.type;
    const skipBoxShadow =
      type === 'radio' || type === 'checkbox' || this.border === false;
    if (!this.isValid && this.touched) {
      if (!skipBoxShadow) {
        this.dom.style.boxShadow = '0 0 0 1px #ef4444';
      } else {
        this.dom.style.boxShadow = '';
      }
      this.dom.classList.add('validation-error');
      this.dom.classList.remove('validation-valid');
    } else {
      if (!skipBoxShadow) {
        this.dom.style.boxShadow = '0 0 0 1px var(--o-hive-dark-20)';
      } else {
        this.dom.style.boxShadow = '';
      }
      this.dom.classList.remove('validation-error');
      this.dom.classList.add('validation-valid');
    }
  }

  setType(value) {
    this.dom.type = value;

    return this;
  }

  setName(value) {
    this.dom.name = value;

    return this;
  }

  setPlaceholder(value) {
    this.dom.placeholder = value;
    return this;
  }

  setMaxLength(value) {
    this.dom.maxLength = value;
    return this;
  }

  getIsValid() {
    return this.isValid;
  }
}

class UIRadio extends UIElement {
  constructor(buttons = [], name = `radioGroup${new Date().toDateString()}`) {
    super(document.createElement('div'));

    this.dom.className = 'RadioGroup flex gap-4 items-center';

    this.name = name;
    this.radioButtons = [];

    this.onChangeCallback = null;

    buttons.forEach(button => {
      this.addOption(button.value, button.label);
    });
  }

  addOption(value, label) {
    const container = new UIDiv();
    container.setClass('flex gap-2 items-center content-center');
    const inputElement = new UIInput();
    inputElement.setType('radio');
    inputElement.setName(this.name);
    inputElement.setValue(value);

    container.add(inputElement);
    container.add(new UIText(label));

    this.add(container);
    this.radioButtons.push(inputElement);

    inputElement.onChange(this.handleChange.bind(this));

    return this;
  }

  handleChange(event) {
    if (this.onChangeCallback) {
      this.onChangeCallback(event.target.value);
    }
  }

  onChange(callback) {
    this.onChangeCallback = callback;
    return this;
  }

  getValue() {
    for (let radio of this.radioButtons) {
      if (radio.getChecked()) {
        return radio.getValue();
      }
    }
    return null;
  }

  setValue(value) {
    for (let radio of this.radioButtons) {
      if (radio.getValue() === value) {
        radio.setChecked(true);
        return this;
      }
    }
    return this;
  }
}

class UITextArea extends UIElement {
  constructor() {
    super(document.createElement('textarea'));

    this.dom.className = 'textarea';
    this.dom.spellcheck = false;

    this.dom.setAttribute('autocomplete', 'off');
  }

  getValue() {
    return this.dom.value;
  }

  setValue(value) {
    this.dom.value = value;

    return this;
  }

  setPlaceholder(value) {
    this.dom.setAttribute('placeholder', value);

    return this;
  }

  setRows(value) {
    this.dom.rows = value;
    return this;
  }
}

class UISelect extends UIElement {
  constructor(placeholder = '') {
    super(document.createElement('select'));
    this.placeholder = placeholder;
    this.dom.className = 'Select';
    this.dom.style.padding = '2px';

    this.dom.setAttribute('autocomplete', 'off');

    if (this.placeholder.length) {
      this.setPlaceholder(this.placeholder);
    }
  }

  setMultiple(boolean) {
    this.dom.multiple = boolean;

    return this;
  }

  setOptions(options, defaultValue, labelKey = '', valueKey = '') {
    const selected = this.dom.value;

    while (this.dom.children.length > 0) {
      this.dom.removeChild(this.dom.firstChild);
    }
    if (this.placeholder.length) {
      this.setPlaceholder(this.placeholder);
    }
    const sortedKeys = Object.keys(options).sort(
      (a, b) => parseFloat(a) - parseFloat(b),
    );
    for (const key of sortedKeys) {
      const option = document.createElement('option');
      if (labelKey.length) {
        option.innerHTML = options[key][labelKey];
      } else {
        option.innerHTML = options[key];
      }
      if (valueKey.length) {
        option.value = options[key][valueKey];
      } else {
        option.value = key;
      }

      this.dom.appendChild(option);
    }

    if (defaultValue && options.hasOwnProperty(defaultValue)) {
      this.dom.value = defaultValue;
    }

    if (selected && options.hasOwnProperty(selected)) {
      this.dom.value = selected;
    }

    return this;
  }

  getValue() {
    return this.dom.value;
  }

  setValue(value) {
    value = String(value);

    if (this.dom.value !== value) {
      this.dom.value = value;
    }

    return this;
  }

  setColorOptions(options) {
    const selected = this.dom.value;
    for (const key in options) {
      const option = document.createElement('option');
      option.value = key;
      option.innerHTML = options[key].label;
      option.className = 'task-status-select-option';
      this.dom.appendChild(option);
    }
    this.dom.value = selected;
    return this;
  }

  setPlaceholder(placeholder) {
    const option = document.createElement('option');
    option.value = '';
    option.innerHTML = placeholder;
    option.disabled = true;
    option.classList.add('hidden');
    this.dom.appendChild(option);
  }
}

class UIDatalist extends UIElement {
  constructor() {
    super(document.createElement('datalist'));
  }

  setId(id) {
    this.dom.id = id;
    return this;
  }

  getId() {
    return this.dom.id;
  }

  setOptions(options) {
    while (this.dom.firstChild) {
      this.dom.removeChild(this.dom.firstChild);
    }

    options.forEach(option => {
      const optionEl = document.createElement('option');
      optionEl.value = option;
      this.dom.appendChild(optionEl);
    });

    return this;
  }
}
class UICheckbox extends UIElement {
  constructor(text, boolean, showLabel = true) {
    super(document.createElement('div'));
    this.dom.className = 'UICheckbox flex gap-2 items-center';

    this.input = document.createElement('input');
    this.input.type = 'checkbox';
    this.input.className = 'UICheckbox-input';
    this.dom.appendChild(this.input);

    if (showLabel) {
      this.label = document.createElement('label');
      this.label.className = 'UICheckbox-label';
      this.label.textContent = text || '';
      this.dom.appendChild(this.label);
    } else {
      this.label = null;
    }

    this.setValue(boolean);
  }

  getValue() {
    return this.input.checked;
  }

  setValue(value) {
    if (value !== undefined) {
      this.input.checked = Boolean(value);
    }
    return this;
  }

  setChecked(isChecked) {
    this.input.checked = Boolean(isChecked);
    return this;
  }

  getChecked() {
    return this.input.checked;
  }

  setLabel(text) {
    if (!this.label) {
      this.label = document.createElement('label');
      this.label.className = 'UICheckbox-label';
      this.dom.appendChild(this.label);
    }
    this.label.textContent = text || '';
    return this;
  }

  getLabel() {
    if (!this.label) return '';
    return this.label.textContent;
  }

  setDisabled(disabled) {
    this.input.disabled = disabled;
    if (this.label) {
      this.label.style.color = disabled ? 'gray' : '';
    }
    return this;
  }
}
class UIColor extends UIElement {
  constructor(customColor) {
    super(document.createElement('input'));

    this.dom.className = 'Color';
    // this.dom.style.width = '32px';
    // this.dom.style.height = '16px';
    this.dom.style.border = 'solid 1px';
    this.dom.style.padding = '1px';
    this.dom.style.backgroundColor = 'transparent';

    this.dom.setAttribute('autocomplete', 'off');

    try {
      this.dom.type = 'color';
      this.dom.value = customColor ?? '#000000';
    } catch (exception) {}
  }

  getValue() {
    return this.dom.value;
  }

  getHexValue() {
    return parseInt(this.dom.value.substr(1), 16);
  }

  setValue(value) {
    this.dom.value = value;

    return this;
  }

  setHexValue(hex) {
    this.dom.value = '#' + ('000000' + hex.toString(16)).slice(-6);

    return this;
  }
}

class UINumber extends UIElement {
  constructor(number) {
    super(document.createElement('input'));

    this.dom.style.cursor = 'ns-resize';
    this.dom.className = 'Number';
    this.dom.value = '0.00';

    this.dom.setAttribute('autocomplete', 'off');

    this.value = 0;

    this.min = -Infinity;
    this.max = Infinity;

    this.precision = 2;
    this.step = 1;
    this.unit = '';
    this.nudge = 0.01;

    this.setValue(number);

    const scope = this;

    const changeEvent = document.createEvent('HTMLEvents');
    changeEvent.initEvent('change', true, true);

    let distance = 0;
    let onMouseDownValue = 0;

    const pointer = { x: 0, y: 0 };
    const prevPointer = { x: 0, y: 0 };

    function onMouseDown(event) {
      event.preventDefault();

      distance = 0;

      onMouseDownValue = scope.value;

      prevPointer.x = event.clientX;
      prevPointer.y = event.clientY;

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    }

    function onMouseMove(event) {
      const currentValue = scope.value;

      pointer.x = event.clientX;
      pointer.y = event.clientY;

      distance += pointer.x - prevPointer.x - (pointer.y - prevPointer.y);

      let value =
        onMouseDownValue + (distance / (event.shiftKey ? 5 : 50)) * scope.step;
      value = Math.min(scope.max, Math.max(scope.min, value));

      if (currentValue !== value) {
        scope.setValue(value);
        scope.dom.dispatchEvent(changeEvent);
      }

      prevPointer.x = event.clientX;
      prevPointer.y = event.clientY;
    }

    function onMouseUp() {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);

      if (Math.abs(distance) < 2) {
        scope.dom.focus();
        scope.dom.select();
      }
    }

    function onTouchStart(event) {
      if (event.touches.length === 1) {
        distance = 0;

        onMouseDownValue = scope.value;

        prevPointer.x = event.touches[0].pageX;
        prevPointer.y = event.touches[0].pageY;

        document.addEventListener('touchmove', onTouchMove);
        document.addEventListener('touchend', onTouchEnd);
      }
    }

    function onTouchMove(event) {
      const currentValue = scope.value;

      pointer.x = event.touches[0].pageX;
      pointer.y = event.touches[0].pageY;

      distance += pointer.x - prevPointer.x - (pointer.y - prevPointer.y);

      let value =
        onMouseDownValue + (distance / (event.shiftKey ? 5 : 50)) * scope.step;
      value = Math.min(scope.max, Math.max(scope.min, value));

      if (currentValue !== value) {
        scope.setValue(value);
        scope.dom.dispatchEvent(changeEvent);
      }

      prevPointer.x = event.touches[0].pageX;
      prevPointer.y = event.touches[0].pageY;
    }

    function onTouchEnd(event) {
      if (event.touches.length === 0) {
        document.removeEventListener('touchmove', onTouchMove);
        document.removeEventListener('touchend', onTouchEnd);
      }
    }

    function onChange() {
      scope.setValue(scope.dom.value);
    }

    function onFocus() {
      scope.dom.style.backgroundColor = '';
      scope.dom.style.cursor = '';
    }

    function onBlur() {
      scope.dom.style.backgroundColor = 'transparent';
      scope.dom.style.cursor = 'ns-resize';
    }

    function onKeyDown(event) {
      event.stopPropagation();

      switch (event.keyCode) {
        case 13: // enter
          scope.dom.blur();
          break;

        case 38: // up
          event.preventDefault();
          scope.setValue(scope.getValue() + scope.nudge);
          scope.dom.dispatchEvent(changeEvent);
          break;

        case 40: // down
          event.preventDefault();
          scope.setValue(scope.getValue() - scope.nudge);
          scope.dom.dispatchEvent(changeEvent);
          break;
      }
    }

    onBlur();

    this.dom.addEventListener('keydown', onKeyDown);
    this.dom.addEventListener('mousedown', onMouseDown);
    this.dom.addEventListener('touchstart', onTouchStart);
    this.dom.addEventListener('change', onChange);
    this.dom.addEventListener('focus', onFocus);
    this.dom.addEventListener('blur', onBlur);
  }

  getValue() {
    return this.value;
  }

  setValue(value) {
    if (value !== undefined) {
      value = parseFloat(value);

      if (value < this.min) value = this.min;
      if (value > this.max) value = this.max;

      this.value = value;
      this.dom.value = value.toFixed(this.precision);

      if (this.unit !== '') this.dom.value += ' ' + this.unit;
    }

    return this;
  }

  setPrecision(precision) {
    this.precision = precision;

    return this;
  }

  setStep(step) {
    this.step = step;

    return this;
  }

  setNudge(nudge) {
    this.nudge = nudge;

    return this;
  }

  setRange(min, max) {
    this.min = min;
    this.max = max;

    return this;
  }

  setUnit(unit) {
    this.unit = unit;

    return this;
  }
}

class UIInteger extends UIElement {
  constructor(number) {
    super(document.createElement('input'));

    this.dom.style.cursor = 'ns-resize';
    this.dom.className = 'Number';
    this.dom.value = '0';

    this.dom.setAttribute('autocomplete', 'off');

    this.value = 0;

    this.min = -Infinity;
    this.max = Infinity;

    this.step = 1;
    this.nudge = 1;

    this.setValue(number);

    const scope = this;

    const changeEvent = document.createEvent('HTMLEvents');
    changeEvent.initEvent('change', true, true);

    let distance = 0;
    let onMouseDownValue = 0;

    const pointer = { x: 0, y: 0 };
    const prevPointer = { x: 0, y: 0 };

    function onMouseDown(event) {
      event.preventDefault();

      distance = 0;

      onMouseDownValue = scope.value;

      prevPointer.x = event.clientX;
      prevPointer.y = event.clientY;

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    }

    function onMouseMove(event) {
      const currentValue = scope.value;

      pointer.x = event.clientX;
      pointer.y = event.clientY;

      distance += pointer.x - prevPointer.x - (pointer.y - prevPointer.y);

      let value =
        onMouseDownValue + (distance / (event.shiftKey ? 5 : 50)) * scope.step;
      value = Math.min(scope.max, Math.max(scope.min, value)) | 0;

      if (currentValue !== value) {
        scope.setValue(value);
        scope.dom.dispatchEvent(changeEvent);
      }

      prevPointer.x = event.clientX;
      prevPointer.y = event.clientY;
    }

    function onMouseUp() {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);

      if (Math.abs(distance) < 2) {
        scope.dom.focus();
        scope.dom.select();
      }
    }

    function onChange() {
      scope.setValue(scope.dom.value);
    }

    function onFocus() {
      scope.dom.style.backgroundColor = '';
      scope.dom.style.cursor = '';
    }

    function onBlur() {
      scope.dom.style.backgroundColor = 'transparent';
      scope.dom.style.cursor = 'ns-resize';
    }

    function onKeyDown(event) {
      event.stopPropagation();

      switch (event.keyCode) {
        case 13: // enter
          scope.dom.blur();
          break;

        case 38: // up
          event.preventDefault();
          scope.setValue(scope.getValue() + scope.nudge);
          scope.dom.dispatchEvent(changeEvent);
          break;

        case 40: // down
          event.preventDefault();
          scope.setValue(scope.getValue() - scope.nudge);
          scope.dom.dispatchEvent(changeEvent);
          break;
      }
    }

    onBlur();

    this.dom.addEventListener('keydown', onKeyDown);
    this.dom.addEventListener('mousedown', onMouseDown);
    this.dom.addEventListener('change', onChange);
    this.dom.addEventListener('focus', onFocus);
    this.dom.addEventListener('blur', onBlur);
  }

  getValue() {
    return this.value;
  }

  setValue(value) {
    if (value !== undefined) {
      value = parseInt(value);

      this.value = value;
      this.dom.value = value;
    }

    return this;
  }

  setStep(step) {
    this.step = parseInt(step);

    return this;
  }

  setNudge(nudge) {
    this.nudge = nudge;

    return this;
  }

  setRange(min, max) {
    this.min = min;
    this.max = max;

    return this;
  }
}

class UIBreak extends UIElement {
  constructor() {
    super(document.createElement('br'));

    this.dom.className = 'Break';
  }
}

class UIHorizontalRule extends UIElement {
  constructor() {
    super(document.createElement('hr'));

    this.dom.className = 'HorizontalRule';
  }
}

class UIButton extends UIElement {
  constructor(value) {
    super(document.createElement('button'));

    this.dom.className = 'Button';
    this.dom.type = 'button';
    this.dom.textContent = value;
  }
}

class UIProgress extends UIElement {
  constructor(value) {
    super(document.createElement('progress'));

    this.dom.value = value;
  }

  setValue(value) {
    this.dom.value = value;
  }
}

class UITabbedPanel extends UIDiv {
  constructor() {
    super();

    this.dom.className = 'TabbedPanel';

    this.tabs = [];
    this.panels = [];

    this.wrapper = new UIDiv();
    this.wrapper.setClass('TabsWrapper');
    // Hidden tabs until required
    this.wrapper.dom.style.display = 'none';

    this.tabsDiv = new UIDiv();
    this.tabsDiv.setClass('Tabs');

    this.panelsDiv = new UIDiv();
    this.panelsDiv.setClass('Panels');

    this.wrapper.add(this.tabsDiv);

    this.add(this.wrapper);
    this.add(this.panelsDiv);

    this.selected = '';
  }

  select(id) {
    let tab;
    let panel;
    const scope = this;

    // Deselect current selection
    if (this.selected && this.selected.length) {
      tab = this.tabs.find(function (item) {
        return item.dom.id === scope.selected;
      });
      panel = this.panels.find(function (item) {
        return item.dom.id === scope.selected;
      });

      if (tab) {
        tab.removeClass('selected');
      }

      if (panel) {
        panel.setDisplay('none');
      }
    }

    tab = this.tabs.find(function (item) {
      return item.dom.id === id;
    });
    panel = this.panels.find(function (item) {
      return item.dom.id === id;
    });

    if (tab) {
      tab.addClass('selected');
    }

    if (panel) {
      panel.setDisplay('');
    }

    this.selected = id;

    return this;
  }

  addTab(id, label, items) {
    const tab = new UITab(label, this);
    tab.setId(id);
    this.tabs.push(tab);
    this.tabsDiv.add(tab);

    const panel = new UIDiv();
    panel.setStyle('height', ['100%']);
    panel.setId(id);
    panel.add(items);
    panel.setDisplay('none');
    this.panels.push(panel);
    this.panelsDiv.add(panel);

    this.select(id);

    if (this.tabs.length > 1) {
      this.wrapper.dom.style.display = 'block';
    }
  }
}

class UITab extends UIText {
  constructor(text, parent) {
    super(text);

    this.dom.className = 'Tab';

    this.parent = parent;

    const scope = this;

    this.dom.addEventListener('click', function () {
      scope.parent.select(scope.dom.id);
    });
  }
}

class UILabel extends UIElement {
  constructor(text) {
    super(document.createElement('label'));

    this.dom.className = 'Label';
    this.dom.textContent = text;
  }

  setFor(id) {
    this.dom.setAttribute('for', id);
  }
}

class UILink extends UIElement {
  constructor({ href, blank } = { href: '', blank: false }) {
    super(document.createElement('a'));

    this.dom.className = 'Link';
    this.dom.href = href;
    if (blank) this.dom.target = '_blank';
  }
}

class UIListbox extends UIDiv {
  constructor() {
    super();

    this.dom.className = 'Listbox';
    this.dom.tabIndex = 0;

    this.items = [];
    this.listitems = [];
    this.selectedIndex = 0;
    this.selectedValue = null;
  }

  setItems(items) {
    if (Array.isArray(items)) {
      this.items = items;
    }

    this.render();
  }

  render() {
    while (this.listitems.length) {
      const item = this.listitems[0];

      item.dom.remove();

      this.listitems.splice(0, 1);
    }

    for (let i = 0; i < this.items.length; i++) {
      const item = this.items[i];

      const listitem = new UIListbox.ListboxItem(this);
      listitem.setId(item.id || `Listbox-${i}`);
      listitem.setTextContent(item.name || item.type);
      this.add(listitem);
    }
  }

  add() {
    const items = Array.from(arguments);

    this.listitems = this.listitems.concat(items);

    UIElement.prototype.add.apply(this, items);
  }

  selectIndex(index) {
    if (index >= 0 && index < this.items.length) {
      this.setValue(this.listitems[index].getId());
    }

    this.selectedIndex = index;
  }

  getValue() {
    return this.selectedValue;
  }

  setValue(value) {
    for (let i = 0; i < this.listitems.length; i++) {
      const element = this.listitems[i];

      if (element.getId() === value) {
        element.addClass('active');
      } else {
        element.removeClass('active');
      }
    }

    this.selectedValue = value;

    const changeEvent = document.createEvent('HTMLEvents');
    changeEvent.initEvent('change', true, true);
    this.dom.dispatchEvent(changeEvent);
  }
}

class ListboxItem extends UIDiv {
  constructor(parent) {
    super();

    this.dom.className = 'ListboxItem';

    this.parent = parent;

    const scope = this;

    function onClick() {
      if (scope.parent) {
        scope.parent.setValue(scope.getId());
      }
    }

    this.dom.addEventListener('click', onClick);
  }
}

const dragEvents = ['DragStart', 'DragEnd', 'Drag'];

class UIImage extends UIElement {
  constructor(src = '') {
    super(document.createElement('img'));
    this.dom.src = src;

    dragEvents.forEach(function (event) {
      const method = 'on' + event;

      UIElement.prototype[method] = function (callback) {
        this.dom.addEventListener(event.toLowerCase(), callback.bind(this));
        return this;
      };
    });
  }

  setAlt(value) {
    this.dom.alt = value;
    return this;
  }
  setSrc(value) {
    this.dom.src = value;
    return this;
  }
}

class UIVideo extends UIElement {
  constructor(src = '') {
    super(document.createElement('video'));
    this.dom.src = src;
    this.dom.preload = 'metadata';
    this.dom.backgroundColor = 'black';
  }

  setAlt(value) {
    this.dom.alt = value;
    return this;
  }
  setSrc(value) {
    this.dom.src = value;
    return this;
  }
  getSrc() {
    return this.dom.src;
  }
  setControls(bool) {
    this.dom.controls = bool;
    return this;
  }
}

class UILoading extends UIDiv {
  constructor() {
    super();
    this.dom.classList.add('lds-ring');
    const div1 = new UIDiv();
    const div2 = new UIDiv();
    const div3 = new UIDiv();
    const div4 = new UIDiv();
    this.add(div1, div2, div3, div4);
  }
}

class UIForm extends UIElement {
  constructor() {
    super(document.createElement('form'));
  }
}

class UICanvas extends UIElement {
  constructor(width = 0, height = 0) {
    super(document.createElement('canvas'));
    this.dom.classList.add('media-canvas');
    this.setDimensions(width, height);
    this._context = null;
  }

  getContext(type = '2d') {
    if (!this._context) {
      this._context = this.dom.getContext(type);
    }
    return this._context;
  }

  setCanvasWidth(width) {
    this.dom.width = width;
    return this; // for chaining
  }

  setCanvasHeight(height) {
    this.dom.height = height;
    return this; // for chaining
  }

  setDimensions(width, height) {
    this.setCanvasWidth(width);
    this.setCanvasHeight(height);
    return this; // for chaining
  }

  getCanvas() {
    return this.dom;
  }

  clear() {
    const ctx = this.getContext();
    ctx.clearRect(0, 0, this.dom.width, this.dom.height);
    return this;
  }

  fill(color) {
    const ctx = this.getContext();
    ctx.fillStyle = color;
    ctx.fillRect(0, 0, this.dom.width, this.dom.height);
    return this;
  }

  createTexture() {
    return new THREE.CanvasTexture(this.dom);
  }

  drawRect(x, y, width, height, color, lineWidth = 1) {
    const ctx = this.getContext();
    ctx.strokeStyle = color;
    ctx.lineWidth = lineWidth;
    ctx.strokeRect(x, y, width, height);
    return this;
  }

  fadeTo(color, duration = 500) {
    return this;
  }

  beginPath() {
    this.getContext().beginPath();
    return this;
  }

  moveTo(x, y) {
    this.getContext().moveTo(x, y);
    return this;
  }

  lineTo(x, y) {
    this.getContext().lineTo(x, y);
    return this;
  }

  stroke(style = 'black', width = 1) {
    const ctx = this.getContext();
    ctx.strokeStyle = style;
    ctx.lineWidth = width;
    ctx.stroke();
    return this;
  }
}

class UIToggle extends UIButton {
  constructor({
    leftValue = new UIElement(),
    rightValue = new UIElement(),
    initialState = false,
  }) {
    super();
    this.leftValue = leftValue;
    this.leftValue.setClass('toggle-left');
    this.leftValue.setStyle('width', ['var(--N2)']);
    this.leftValue.setStyle('height', ['var(--N2)']);
    this.rightValue = rightValue;
    this.rightValue.setClass('toggle-right');
    this.rightValue.setStyle('width', ['var(--N2)']);
    this.rightValue.setStyle('height', ['var(--N2)']);
    this.state = initialState;

    this.setStyle('display', ['flex']);
    this.setStyle('align-items', ['center']);
    this.setStyle('justify-content', ['center']);
    this.setStyle('cursor', ['pointer']);
    this.setStyle('user-select', ['none']);

    const divider = new UIDiv();
    divider.setClass('divider');
    divider.setStyle('width', ['1px']);
    divider.setStyle('height', ['var(--N1)']);
    divider.setStyle('margin', ['0', 'var(--N3)']);
    divider.setStyle('background-color', ['#ccc']);
    this.add(leftValue, divider, rightValue);

    this.updateOpacity();

    this.onClick(() => {
      this.toggle();
    });
  }

  toggle() {
    this.state = !this.state;
    this.updateOpacity();
  }

  updateOpacity() {
    if (this.state) {
      this.leftValue.setStyle('opacity', ['1']);
      this.rightValue.setStyle('opacity', ['0.5']);
    } else {
      this.leftValue.setStyle('opacity', ['0.5']);
      this.rightValue.setStyle('opacity', ['1']);
    }
  }
}

class UISVG extends UIElement {
  constructor(svgUrl, color, size, strokeColor = null) {
    super(document.createElement('div'));
    if (size) {
      this.setStyle('width', [size + 'px']);
      this.setStyle('height', [size + 'px']);
    }
    this.setSVG(svgUrl, color, strokeColor);
  }

  async setSVG(svgUrl, color, strokeColor) {
    try {
      const response = await fetch(svgUrl);
      if (!response.ok) {
        throw new Error('SVG not readable');
      }

      // Fetch as a blob and convert to text
      const blob = await response.blob();
      const svgContent = await blob.text();

      if (!svgContent.trim().startsWith('<svg')) {
        throw new Error('Response does not contain valid SVG markup');
      }

      const svgElement = new DOMParser().parseFromString(
        svgContent,
        'image/svg+xml',
      ).documentElement;

      if (svgElement.nodeName === 'parsererror') {
        throw new Error('Error parsing SVG: ' + svgElement.textContent);
      }

      const elements = svgElement.querySelectorAll('*');
      elements.forEach(el => el.removeAttribute('fill'));
      svgElement.setAttribute('fill', color);
      if (strokeColor) {
        svgElement.setAttribute('stroke', strokeColor);
      }
      svgElement.setAttribute('width', '100%');
      svgElement.setAttribute('height', '100%');
      this.dom.innerHTML = '';
      this.dom.appendChild(svgElement);
    } catch (error) {
      console.error('Error fetching or parsing SVG:', error);
    }
  }
}

class UITable extends UIElement {
  constructor(defaultStyles = true) {
    super(document.createElement('table'));
    this.defaultStyles = defaultStyles;
    if (this.defaultStyles) {
      this.setClass('min-w-full divide-y divide-gray-200 overflow-hidden');
    }
  }

  createHeader(...columnNames) {
    const thead = document.createElement('thead');
    if (this.defaultStyles) {
      thead.className = 'bg-gray-50';
    }
    const tr = document.createElement('tr');

    columnNames.forEach(name => {
      const th = document.createElement('th');
      if (this.defaultStyles) {
        th.className =
          'px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap';
      }
      th.textContent = name;
      tr.appendChild(th);
    });

    thead.appendChild(tr);
    this.dom.appendChild(thead);
    return this;
  }

  createBody() {
    const tbody = document.createElement('tbody');
    if (this.defaultStyles) {
      tbody.className = 'bg-white divide-y divide-gray-200';
    }
    this.dom.appendChild(tbody);
    return new UIElement(tbody);
  }

  addRow(...cells) {
    const tbody = this.find('tbody')[0] || this.createBody();
    const tr = new UIElement(document.createElement('tr'));
    if (this.defaultStyles) {
      tr.setClass('hover:bg-gray-50 transition-colors');
    }

    cells.forEach(cell => {
      const td = new UIElement(document.createElement('td'));
      if (this.defaultStyles) {
        td.setClass('px-6 py-4 whitespace-nowrap text-sm text-gray-900');
      }
      if (cell instanceof UIElement) {
        td.add(cell);
      } else {
        td.setTextContent(cell);
      }
      tr.add(td);
    });

    tbody.add(tr);
    return this;
  }
}

class UITag extends UIDiv {
  constructor({
    src = '',
    status,
    parameters,
    type,
    commentId,
    position,
    parent,
    pdfPage,
    message,
  }) {
    super();

    this.dom.className = 'TagHtml';
    this.id = commentId;
    this.title = `tag-${commentId}`;
    this.position = position;
    this.parent = parent;
    this.is2d = true;

    this.bgImg = new UIDiv();
    this.bgImg.dom.className = 'TagHtmlImage';

    this.tagSpan = new UISpan().addClass('tag-span');

    this.dom.dataset.tag2d = true;
    this.dom.dataset.status = status ?? '';
    this.dom.dataset.message = message ?? '';
    this.dom.dataset.parameters = JSON.stringify(parameters ?? {});
    this.dom.dataset.type = type ?? '';
    this.dom.dataset.tagId = commentId;
    this.dom.dataset.parentId = parent.cadFileModelId;
    this.dom.dataset.positionX = position.x;
    this.dom.dataset.positionY = position.y;
    if (parent.userData.isPdf) {
      this.dom.dataset.isPdf = true;
      this.dom.dataset.pdfURL = parent.pdfURL;
      this.dom.dataset.pdfPage = pdfPage ?? 1;
    }

    this.dom.appendChild(this.bgImg.dom);
    this.dom.appendChild(this.tagSpan.dom);

    if (src && src !== '') this.setBackgroundImage(src);
  }

  remove() {
    if (this.dom && this.dom.parentNode) {
      this.dom.remove();
      this.dom = null;
    }
  }

  toggle(_bool) {
    this.dom.classList.toggle('hidden', !_bool);
  }

  changeColor(_color) {
    this.dom.style.backgroundColor = _color;
  }

  changeValue(_val) {
    this.tagSpan.dom.textContent = _val;
  }

  setBackgroundImage(_src) {
    this.bgImg.dom.appendChild(_src);
  }

  setPosition(x, y, size = 1) {
    this.dom.style.transform = `translateX(${x}px) translateY(${y}px) scale(${size})`;

    return this;
  }
}

class UIParagraph extends UIElement {
  constructor(value) {
    super(document.createElement('p'));
    this.dom.textContent = value;
  }
}

class UIUnorderedList extends UIElement {
  constructor(value) {
    super(document.createElement('ul'));
  }
}

class UIOrderedList extends UIElement {
  constructor(value) {
    super(document.createElement('ol'));
  }
}

class UIListItem extends UIElement {
  constructor(value) {
    super(document.createElement('li'));
  }
}

export {
  ListboxItem,
  UIBreak,
  UIButton,
  UICanvas,
  UICheckbox,
  UIColor,
  UIDatalist,
  UIDiv,
  UIElement,
  UIForm,
  UIHorizontalRule,
  UIImage,
  UIInput,
  UIInteger,
  UILabel,
  UILink,
  UIListItem,
  UILoading,
  UINumber,
  UIOrderedList,
  UIPanel,
  UIParagraph,
  UIProgress,
  UIRadio,
  UIRow,
  UISelect,
  UISpan,
  UISVG,
  UITabbedPanel,
  UITable,
  UITag,
  UIText,
  UITextArea,
  UIToggle,
  UIUnorderedList,
  UIVideo,
};
